/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore, acceptHMRUpdate } from 'pinia';
import isEmpty from 'lodash/isEmpty';
import { loadLabels, updateLabel, deleteLabel, updateCategory, deleteCategory } from '@/voc/api/labels.api';
import { runAggregationOnce, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import LabelAssociatedItemCountsAggregation from '@/voc/aggregations/label-associated-item-counts/label-associated-item-counts.aggregation';

const { GENERAL } = SUB_PRODUCT_AREAS || {};

interface Label {
    id: string;
    category?: { id: string; [key: string]: unknown };
    associatedItemCount?: number;
    [key: string]: unknown;
}

interface LabelCount {
    labelId: string;
    type: string;
    count: number;
}

interface VocLabelsState {
    labelOptions: Label[];
    labelCounts: LabelCount[];
    promises: {
        labelOptions: Promise<void> | null;
    };
}

export function getInitialState (): VocLabelsState {
    return {
        labelOptions: [],
        labelCounts: [],
        promises: {
            labelOptions: null,
        },
    };
}

export const useVocLabelsStore = defineStore('voc/labels', {
    state: getInitialState,
    getters: {
        labels: (state) => state.labelOptions,
        ideaLabels: (state) => state.labelOptions.map((label) => ({
            ...label,
            associatedItemCount: state.labelCounts.find((itemCount) => itemCount.labelId === label.id && itemCount.type === 'idea')?.count || 0,
        })),
        feedbackLabels: (state) => state.labelOptions.map((label) => ({
            ...label,
            associatedItemCount: state.labelCounts.find((itemCount) => itemCount.labelId === label.id && itemCount.type === 'feedback')?.count || 0,
        })),
    },
    actions: {
        setLabels ({ labels }: { labels: Label[] }) {
            this.labelOptions = labels;
        },
        setLabelCounts ({ labelCounts }: { labelCounts: LabelCount[] }) {
            this.labelCounts = labelCounts;
        },
        setPromise ({ key, promise }: { key: keyof VocLabelsState['promises']; promise: Promise<void> | null }) {
            this.promises[key] = promise;
        },
        loadLabels ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (this.promises.labelOptions) {
                return this.promises.labelOptions;
            }

            if (!isEmpty(this.labelOptions) && !reFetch) {
                return Promise.resolve();
            }

            LabelAssociatedItemCountsAggregation.subProductArea = GENERAL;
            const labelPromise = loadLabels();
            const associatedItemCountPromise = runAggregationOnce(LabelAssociatedItemCountsAggregation);

            const promise = Promise.all([labelPromise, associatedItemCountPromise])
                .then(([labels, associatedItemCounts]) => {
                    this.setLabels({ labels });
                    this.setLabelCounts({ labelCounts: associatedItemCounts as LabelCount[] });
                })
                .finally(() => {
                    this.setPromise({ key: 'labelOptions', promise: null });
                });

            this.setPromise({ key: 'labelOptions', promise });

            return promise;
        },
        async updateLabel ({ labelId, name, categoryId }: { labelId: string; name: string; categoryId?: string }) {
            const editedLabel = await updateLabel(labelId, name, categoryId as any);
            const labels = this.labelOptions.map((label) => {
                if (label.id === editedLabel.id) {
                    return {
                        ...editedLabel,
                        associatedItemCount: label.associatedItemCount,
                    };
                }

                return label;
            });
            this.setLabels({ labels });

            return editedLabel;
        },
        async deleteLabel ({ labelId }: { labelId: string }) {
            await deleteLabel(labelId);
            const labels = this.labelOptions.filter((label) => label.id !== labelId);

            this.setLabels({ labels });

            return labelId;
        },
        async updateCategory ({ categoryId, name }: { categoryId: string; name: string }) {
            const editedCategory = await updateCategory(categoryId, name);
            const labels = this.labelOptions.map((label) => {
                if (label.category?.id === editedCategory.id) {
                    return {
                        ...label,
                        category: editedCategory,
                    };
                }

                return label;
            });
            this.setLabels({ labels });

            return editedCategory;
        },
        async deleteCategory ({ categoryId }: { categoryId: string }) {
            await deleteCategory(categoryId);
            const labels = this.labelOptions.filter((label) => label.category?.id !== categoryId);

            this.setLabels({ labels });

            return categoryId;
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useVocLabelsStore, module.hot));
}
